import {
  HomeLayoutFragment,
  HomeServicesGrid,
  HomeServicesHeader,
  HomeWelcomeContainer,
  HomeWelcomeDate,
  HomeWelcomeQuickLink,
  HomeWelcomeQuickLinkBar,
  HomeWelcomeTitle,
} from './Home.styles';
import { ConstructSharp, Wifi } from 'react-ionicons';
import format from 'date-fns/format';
import { ReactComponent as Footsteps } from '../../assets/icon/footsteps.svg';
import CompanyName from 'components/Navbar/CompanyName';
import { useAuthUser } from 'hooks/useAuthUser';
import { TokenUserPermissions } from '../../api/users/schemas/Users';
import { CompanyAdminsDialog } from './CompanyAdminsDialog';
import { useModal } from 'hooks/useModal';
import { DisplayForPermission } from 'components/Permission/DisplayForPermission';
import { usePermissions } from 'hooks/usePermissions';
import InvoiceAnalyzer from 'screens/InvoiceAnalyzer';
import HomeServiceCard from './HomeServiceCard';
import { usePillarLogoByMode } from 'hooks/usePillarLogoByMode';

const Home = () => {
  const { name, companyName } = useAuthUser();

  const { open, ...props } = useModal();

  const { hasPermission } = usePermissions({
    permission: [TokenUserPermissions.ACCESS_EXPRESS_MANAGE],
  });

  const { accessexpressLogo, nocexpressLogo, techexpressLogo } =
    usePillarLogoByMode();

  return (
    <div className="flex w-full flex-col items-start justify-start gap-6 px-4 py-8 lg:px-8">
      <HomeWelcomeContainer className="gap-2 bg-background-base-surface-2/70 px-6 py-8 md:py-9 ">
        <HomeWelcomeDate className="text-sm leading-snug text-content-base-default sm:text-base">
          {format(new Date(), 'eeee, MMMM do, yyyy')}
        </HomeWelcomeDate>
        <HomeWelcomeTitle className="text-4xl text-content-base-default md:text-5xl lg:text-[52px] lg:!leading-tight">
          Welcome, {name ?? ''}
        </HomeWelcomeTitle>
        <CompanyName name={companyName} textClasses="text-xl md:text-2xl" />
        <DisplayForPermission
          permission={[
            TokenUserPermissions.ACCESS_EXPRESS_WRITE,
            TokenUserPermissions.TECH_EXPRESS_WRITE,
            TokenUserPermissions.NOC_EXPRESS_WRITE,
          ]}
        >
          <HomeWelcomeQuickLinkBar className="mt-6 flex-wrap md:mt-8">
            <DisplayForPermission
              permission={TokenUserPermissions.ACCESS_EXPRESS_WRITE}
            >
              <HomeWelcomeQuickLink
                to="/access-express/v3/order-services/access"
                className="!w-full sm:!w-auto"
              >
                <Wifi width="24px" height="24px" />
                Order services
              </HomeWelcomeQuickLink>
            </DisplayForPermission>
            <DisplayForPermission
              permission={TokenUserPermissions.TECH_EXPRESS_WRITE}
            >
              <HomeWelcomeQuickLink
                to="/tech-express/let-us-help"
                className="!w-full sm:!w-auto"
              >
                <Footsteps />
                Request dispatch
              </HomeWelcomeQuickLink>
            </DisplayForPermission>
            <DisplayForPermission
              permission={TokenUserPermissions.NOC_EXPRESS_WRITE}
            >
              <HomeWelcomeQuickLink
                to="/noc-express/open-ticket"
                className="!w-full sm:!w-auto"
              >
                <ConstructSharp width="24px" height="24px" color="inherit" />
                Open repair ticket
              </HomeWelcomeQuickLink>
            </DisplayForPermission>
          </HomeWelcomeQuickLinkBar>
        </DisplayForPermission>
      </HomeWelcomeContainer>
      <InvoiceAnalyzer type="dashboard" />
      <HomeLayoutFragment>
        <HomeServicesHeader className="text-xl leading-tight md:text-[28px]">
          Your services
        </HomeServicesHeader>
        <HomeServicesGrid>
          <HomeServiceCard
            permission={TokenUserPermissions.ACCESS_EXPRESS_READ}
            pillarAltImg="Access Express logo"
            pillarSrc={accessexpressLogo}
            to="/access-express/v3"
            variant={`${hasPermission ? 'secondary' : 'primary'}`}
            content="Browse and order Granite services for your locations."
            buttonContent={hasPermission ? 'Go to orders' : 'View your orders'}
          />
          <HomeServiceCard
            permission={TokenUserPermissions.TECH_EXPRESS_READ}
            pillarAltImg="Tech Express Logo"
            pillarSrc={techexpressLogo}
            to="/tech-express"
            content="Request a service technician to any Granite-serviced location."
            buttonContent="View your tickets"
          />

          <HomeServiceCard
            permission={TokenUserPermissions.NOC_EXPRESS_READ}
            pillarAltImg="NOC Express Logo"
            pillarSrc={nocexpressLogo}
            to="/noc-express"
            content="View all repair tickets and a personalized dashboard for your Granite service performance."
            buttonContent="View your tickets"
          />
        </HomeServicesGrid>
      </HomeLayoutFragment>
      <CompanyAdminsDialog {...props} />
    </div>
  );
};

export default Home;
